import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { submitKangstudyForm, formDataToRecord } from "@/lib/submit-form";
import { motion, AnimatePresence } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const consultationTypes = [
  { key: "1on1", label: "1:1 상담", icon: "ri-user-voice-line", desc: "강현우 강사님과 1:1로 진행하는 맞춤형 경매 투자 상담" },
  { key: "analysis", label: "물건분석 상담", icon: "ri-file-search-line", desc: "관심 있는 경매 물건을 가져오시면 권리분석부터 입찰 전략까지 알려드립니다" },
  { key: "business", label: "매매사업자 상담", icon: "ri-building-line", desc: "매매사업자를 위한 세무, 자금조달, 물건선정 전략 상담" },
  { key: "emergency", label: "긴급 전화상담", icon: "ri-phone-fill", desc: "입찰 직전, 15분 원포인트 핵심 체크로 리스크를 잡아드립니다", price: "39,000원" },
];

const timeSlots = [
  "오전 9:00 - 10:00",
  "오전 10:00 - 11:00",
  "오전 11:00 - 12:00",
  "오후 2:00 - 3:00",
  "오후 3:00 - 4:00",
  "오후 4:00 - 5:00",
  "오후 7:00 - 8:00",
  "오후 8:00 - 9:00",
];

const investmentPurposes = [
  { key: "residence", label: "실거주" },
  { key: "profit", label: "시세차익" },
  { key: "business", label: "매매사업자" },
];

interface TimeSlotData {
  date: string;
  label: string;
  time: string;
  dayOfWeek: string;
}

function formatDateKR(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${y}년 ${m}월 ${d}일`;
}

function getDayOfWeekKR(date: Date): string {
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  return days[date.getDay()];
}

function getEmergencySlots(): TimeSlotData[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const slots: TimeSlotData[] = [];

  const todayDow = today.getDay();
  if (todayDow >= 1 && todayDow <= 5) {
    const todayStr = formatDateKR(today);
    const todayDayKr = getDayOfWeekKR(today);
    if (now.getHours() < 20 || (now.getHours() === 20 && now.getMinutes() < 30)) {
      slots.push({ date: todayStr, label: "오늘 저녁", time: "20:00", dayOfWeek: todayDayKr });
      slots.push({ date: todayStr, label: "오늘 저녁", time: "20:30", dayOfWeek: todayDayKr });
      slots.push({ date: todayStr, label: "오늘 저녁", time: "21:00", dayOfWeek: todayDayKr });
    }
  }

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowDow = tomorrow.getDay();
  if (tomorrowDow >= 1 && tomorrowDow <= 5) {
    const tomorrowStr = formatDateKR(tomorrow);
    const tomorrowDayKr = getDayOfWeekKR(tomorrow);
    slots.push({ date: tomorrowStr, label: "내일 아침", time: "08:30", dayOfWeek: tomorrowDayKr });
    slots.push({ date: tomorrowStr, label: "내일 아침", time: "09:00", dayOfWeek: tomorrowDayKr });
  }

  return slots;
}

const consultationReviews: Record<string, { text: string; name: string }[]> = {
  "1on1": [
    { text: "1:1 상담 후 3개월 만에 첫 낙찰 성공! 강사님 조언이 진짜 컸어요", name: "김민수" },
    { text: "혼자 고민하던 투자 방향을 딱 잡아주셨어요. 상담받길 잘했어요", name: "이지은" },
    { text: "노후 준비 막막했는데 구체적인 플랜을 짜주셔서 감사합니다", name: "박성호" },
  ],
  "analysis": [
    { text: "리포트 덕분에 하자 있는 물건 걸러내고 2천만원 아꼈어요", name: "김태호" },
    { text: "등기부등본 분석이 이렇게 명쾌할 수 있나 싶었습니다. 강추!", name: "정대호" },
    { text: "물건분석 상담받고 불안했던 입찰, 확신 가지고 진행했습니다", name: "한소희" },
  ],
  "business": [
    { text: "매매사업자 전략 상담 후 수익률이 30% 이상 올랐어요", name: "최유진" },
    { text: "세무 전략까지 챙겨주셔서 사업자 등록할 때 큰 도움 됐습니다", name: "사업자준비중" },
    { text: "물건 선정부터 매도 타이밍까지, 진짜 파트너 같은 상담이었어요", name: "실전투자자" },
  ],
  "emergency": [
    { text: "입찰 30분 전에 전화했는데 핵심만 딱 짚어주셨어요. 진짜 살았습니다", name: "첫낙찰도전" },
    { text: "급하게 물건 분석 부탁드렸는데 15분 만에 위험 요소 다 찾아주셨어요", name: "초보투자자" },
    { text: "입찰 당일 아침에 상담받고 과감히 포기했는데, 그 물건 명도소송 났대요", name: "알뜰투자자" },
  ],
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardItem = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } },
};

const formFieldItem = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" as const } },
};

export default function ConsultationPage() {
  const [consultType, setConsultType] = useState("1on1");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    time: "",
    message: "",
    caseNumber: "",
    investmentPurpose: "",
    bidAmount: "",
    concern: "",
    agreePrivacy: false,
  });
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [emergencySlot, setEmergencySlot] = useState<string | null>(null);
  const emergencySlots = useMemo(() => getEmergencySlots(), []);
  const formRef = useRef<HTMLFormElement>(null);

  const [reviewIndices, setReviewIndices] = useState<Record<string, number>>({
    "1on1": 0,
    "analysis": 0,
    "business": 0,
    "emergency": 0,
  });

  useEffect(() => {
    const intervals = consultationTypes.map((t) => {
      return setInterval(() => {
        setReviewIndices((prev) => {
          const reviews = consultationReviews[t.key];
          if (!reviews || reviews.length === 0) return prev;
          return { ...prev, [t.key]: (prev[t.key] + 1) % reviews.length };
        });
      }, 4000);
    });
    return () => intervals.forEach(clearInterval);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const isEmergency = consultType === "emergency";
  const isStep1Valid = consultType !== "";
  const isStep2ValidRegular =
    form.name.trim() !== "" &&
    form.phone.trim() !== "" &&
    form.email.trim() !== "" &&
    form.date !== "" &&
    form.time !== "" &&
    form.caseNumber.trim() !== "" &&
    form.investmentPurpose !== "" &&
    form.agreePrivacy;
  const isStep2ValidEmergency =
    emergencySlot !== null &&
    form.caseNumber.trim() !== "" &&
    form.bidAmount.trim() !== "" &&
    form.concern.trim() !== "";
  const isStep2Valid = isEmergency ? isStep2ValidEmergency : isStep2ValidRegular;

  const emergencySlotData = emergencySlots.find((s) => `${s.date}|${s.time}` === emergencySlot);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!isStep2Valid) return;

    const formElement = formRef.current;
    if (!formElement) return;

    const formData = new FormData(formElement);

    const honeypotValue = (formData.get("phone_alt") as string || "").trim();
    if (honeypotValue !== "") {
      setSubmitStatus("success");
      return;
    }

    if (!isEmergency) {
      formData.append("consult_type", consultationTypes.find((t) => t.key === consultType)?.label || consultType);
      formData.append("preferred_date", form.date);
      formData.append("preferred_time", form.time);
    } else {
      formData.append("consult_type", "긴급 전화상담 (15분)");
      if (emergencySlotData) {
        formData.append("emergency_slot", `${emergencySlotData.date}(${emergencySlotData.dayOfWeek}) ${emergencySlotData.time}`);
      }
    }

    setSubmitStatus("loading");

    const { ok, error } = await submitKangstudyForm(
      "consultation",
      formDataToRecord(formData),
    );
    if (ok) {
      setSubmitStatus("success");
    } else {
      setFormError(error);
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus("idle"), 3000);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const resetForm = () => {
    setSubmitStatus("idle");
    setFormError("");
    setStep(1);
    setForm({ name: "", phone: "", email: "", date: "", time: "", message: "", caseNumber: "", investmentPurpose: "", bidAmount: "", concern: "", agreePrivacy: false });
    setConsultType("1on1");
    setEmergencySlot(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[960px] mx-auto">
          {/* Page Header */}
          <motion.div
            className="mb-10 text-center"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" as const }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <i className="ri-customer-service-2-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">상담신청</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-3">
              전문가 상담 신청
            </h1>
            <p className="text-sm text-[#8C8780] max-w-[500px] mx-auto leading-relaxed">
              초보자도 쉽게 따라오는 경매 투자, 강스터디가 함께합니다. 원하시는 상담 유형을 선택하고 편한 시간에 상담을 신청해보세요.
            </p>
          </motion.div>

          {/* Expert Profile Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" as const, delay: 0.1 }}
            className="mb-8 bg-white rounded-2xl border border-[#E8E5E0] p-5 md:p-6 flex flex-col sm:flex-row items-center gap-5"
          >
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#C4956A]/20">
              <img loading="lazy" decoding="async"
                src="https://img.echotwin.kr/kangstudy/ecd2f3483806a831.jpg"
                alt="강현우 강사 프로필"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg font-bold text-[#1C1917] mb-1">강현우 강사</h3>
              <p className="text-sm text-[#8C8780] mb-3">경매 투자 전문가 · 10년 이상 강의 경력</p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#C4956A]/10 rounded-full text-xs font-medium text-[#C4956A] whitespace-nowrap">
                  <i className="ri-award-line text-sm"></i>
                  누적 낙찰 300건+
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 rounded-full text-xs font-medium text-emerald-600 whitespace-nowrap">
                  <i className="ri-star-fill text-sm text-amber-400"></i>
                  고객 만족도 ★4.9 / 5.0
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-50 rounded-full text-xs font-medium text-rose-500 whitespace-nowrap">
                  <i className="ri-user-heart-line text-sm"></i>
                  수강생 2,000명+
                </span>
              </div>
            </div>
          </motion.div>

          {/* Success State */}
          <AnimatePresence mode="wait">
            {submitStatus === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="bg-white rounded-2xl border border-[#E8E5E0] p-10 md:p-14 text-center"
              >
                <motion.div
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-50 flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.2 }}
                >
                  <i className="ri-check-line text-3xl text-emerald-500"></i>
                </motion.div>
                <motion.h2
                  className="text-xl font-bold text-[#1C1917] mb-3"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  {isEmergency ? "긴급 상담이 예약되었습니다!" : "상담 신청이 완료되었습니다!"}
                </motion.h2>
                <motion.p
                  className="text-sm text-[#8C8780] mb-2 leading-relaxed"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  {isEmergency
                    ? `${emergencySlotData?.date} (${emergencySlotData?.dayOfWeek}요일) ${emergencySlotData?.time}에 전화드리겠습니다.`
                    : "입력하신 연락처로 24시간 이내에 상담 일정 안내를 드리겠습니다."}
                </motion.p>
                <motion.p
                  className="text-xs text-[#B8B3AE]"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                >
                  긴급한 문의는 카카오톡 채널 '강스터디'로 연락 주세요.
                </motion.p>
                <motion.button
                  onClick={resetForm}
                  className="mt-8 px-6 py-2.5 text-sm font-medium text-[#C4956A] border border-[#C4956A] rounded-full hover:bg-[#C4956A] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.4 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  다른 상담 신청하기
                </motion.button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" as const, delay: 0.1 }}
                className="bg-white rounded-2xl border border-[#E8E5E0] overflow-hidden"
              >
                {/* Step Indicator */}
                <div className="flex items-center border-b border-[#F0EDE8]">
                  <button
                    onClick={() => setStep(1)}
                    className={`flex-1 py-4 text-center text-sm font-medium transition-colors relative cursor-pointer ${
                      step === 1 ? "text-[#1C1917]" : "text-[#B8B3AE] hover:text-[#5C5650]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${
                        step === 1 ? "bg-[#1C1917] text-white" : "bg-[#F0EDE8] text-[#8C8780]"
                      }`}>1</span>
                      상담 유형 선택
                    </span>
                    {step === 1 && (
                      <motion.div
                        layoutId="stepIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1C1917]"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => { if (isStep1Valid) setStep(2); }}
                    className={`flex-1 py-4 text-center text-sm font-medium transition-colors relative cursor-pointer ${
                      step === 2 ? "text-[#1C1917]" : "text-[#B8B3AE]"
                    } ${!isStep1Valid ? "cursor-not-allowed opacity-50" : "hover:text-[#5C5650]"}`}
                    disabled={!isStep1Valid}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${
                        step === 2 ? "bg-[#1C1917] text-white" : "bg-[#F0EDE8] text-[#8C8780]"
                      }`}>2</span>
                      정보 입력
                    </span>
                    {step === 2 && (
                      <motion.div
                        layoutId="stepIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1C1917]"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  {/* Step 1: Select Consultation Type */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 30 }}
                      transition={{ duration: 0.35, ease: "easeOut" as const }}
                      className="p-6 md:p-8"
                    >
                      <motion.p
                        className="text-sm text-[#8C8780] mb-6"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                      >
                        원하시는 상담 유형을 선택해주세요
                      </motion.p>

                      <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        variants={cardStagger}
                        initial="hidden"
                        animate="visible"
                      >
                        {consultationTypes.map((t) => {
                          const reviews = consultationReviews[t.key] || [];
                          const currentReviewIdx = reviewIndices[t.key] || 0;
                          return (
                            <motion.button
                              key={t.key}
                              variants={cardItem}
                              onClick={() => setConsultType(t.key)}
                              whileHover={{ y: -4, borderColor: "#C4956A" }}
                              whileTap={{ scale: 0.98 }}
                              className={`p-5 rounded-xl border-2 text-left transition-colors cursor-pointer flex flex-col ${
                                consultType === t.key
                                  ? "border-[#C4956A] bg-[#C4956A]/5"
                                  : "border-[#E8E5E0] bg-white hover:border-[#C4956A]/30"
                              }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="w-10 h-10 rounded-lg bg-[#FAFAF8] flex items-center justify-center">
                                  <i className={`${t.icon} text-lg text-[#C4956A]`}></i>
                                </div>
                                {"price" in t && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 text-rose-500 text-xs font-bold rounded-full whitespace-nowrap">
                                    <i className="ri-flashlight-line"></i>
                                    {t.price}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-sm font-bold text-[#1C1917] mb-1.5">{t.label}</h3>
                              <p className="text-xs text-[#8C8780] leading-relaxed mb-4">{t.desc}</p>

                              {/* Review Slider */}
                              {reviews.length > 0 && (
                                <div className="mt-auto pt-4 border-t border-[#F0EDE8]">
                                  <AnimatePresence mode="wait">
                                    <motion.div
                                      key={currentReviewIdx}
                                      initial={{ opacity: 0, y: 6 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: -6 }}
                                      transition={{ duration: 0.3 }}
                                      className="flex items-start gap-2"
                                    >
                                      <i className="ri-double-quotes-l text-[#C4956A]/40 text-sm mt-0.5 flex-shrink-0"></i>
                                      <div className="min-w-0">
                                        <p className="text-[11px] text-[#5C5650] leading-relaxed line-clamp-2">
                                          {reviews[currentReviewIdx].text}
                                        </p>
                                        <p className="text-[10px] text-[#B8B3AE] mt-1">
                                          {reviews[currentReviewIdx].name}님
                                        </p>
                                      </div>
                                    </motion.div>
                                  </AnimatePresence>
                                  <div className="flex items-center gap-1 mt-2">
                                    {reviews.map((_, i) => (
                                      <span
                                        key={i}
                                        className={`block w-1.5 h-1.5 rounded-full transition-colors ${
                                          i === currentReviewIdx ? "bg-[#C4956A]" : "bg-[#E8E5E0]"
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </motion.button>
                          );
                        })}
                      </motion.div>

                      <motion.div
                        className="mt-8 flex justify-end"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.4 }}
                      >
                        <motion.button
                          onClick={() => setStep(2)}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="px-8 py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap cursor-pointer"
                        >
                          다음 단계
                          <i className="ri-arrow-right-line ml-2"></i>
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* Step 2: Regular Form (1on1, analysis, business) */}
                  {step === 2 && !isEmergency && (
                    <motion.form
                      key="step2-regular"
                      ref={formRef}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35, ease: "easeOut" as const }}
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="p-6 md:p-8"
                    >
                      <motion.button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex items-center gap-1.5 text-sm text-[#8C8780] hover:text-[#C4956A] transition-colors mb-6 cursor-pointer"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                      >
                        <i className="ri-arrow-left-line"></i>
                        상담 유형 다시 선택
                      </motion.button>

                      <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 gap-5"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {/* Name */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            이름 <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="이름을 입력해주세요"
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                            required
                          />
                        </motion.div>

                        {/* Phone */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            연락처 <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="010-0000-0000"
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                            required
                          />
                        </motion.div>

                        {/* Email */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            이메일 <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="example@email.com"
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                            required
                          />
                        </motion.div>

                        {/* Selected Type (read-only) */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            상담 유형
                          </label>
                          <div className="w-full px-4 py-2.5 text-sm border border-[#F0EDE8] rounded-lg bg-[#FAFAF8] text-[#C4956A] font-medium">
                            {consultationTypes.find((ct) => ct.key === consultType)?.label}
                          </div>
                        </motion.div>

                        {/* Case Number - NEW */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            관심 경매 사건번호 <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            name="caseNumber"
                            value={form.caseNumber}
                            onChange={handleChange}
                            placeholder="예: 2026타경1234"
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                            required
                          />
                        </motion.div>

                        {/* Investment Purpose - NEW */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            투자 목적 <span className="text-red-400">*</span>
                          </label>
                          <select
                            name="investmentPurpose"
                            value={form.investmentPurpose}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] focus:outline-none focus:border-[#C4956A] cursor-pointer transition-colors"
                            required
                          >
                            <option value="">투자 목적을 선택해주세요</option>
                            {investmentPurposes.map((p) => (
                              <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                          </select>
                        </motion.div>

                        {/* Date */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            희망 날짜 <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="date"
                            name="date"
                            value={form.date}
                            onChange={handleChange}
                            min={today}
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                            required
                          />
                        </motion.div>

                        {/* Time */}
                        <motion.div variants={formFieldItem}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            희망 시간 <span className="text-red-400">*</span>
                          </label>
                          <select
                            name="time"
                            value={form.time}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] focus:outline-none focus:border-[#C4956A] cursor-pointer transition-colors"
                            required
                          >
                            <option value="">시간을 선택해주세요</option>
                            {timeSlots.map((slot) => (
                              <option key={slot} value={slot}>{slot}</option>
                            ))}
                          </select>
                        </motion.div>

                        {/* Message - full width */}
                        <motion.div variants={formFieldItem} className="md:col-span-2">
                          <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                            상담 내용 / 문의사항
                          </label>
                          <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="상담받고 싶은 내용이나 궁금한 점을 자유롭게 적어주세요"
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors resize-none"
                          ></textarea>
                          <p className="text-xs text-[#B8B3AE] mt-1">{form.message.length}/500</p>
                        </motion.div>
                      </motion.div>

                      {/* Privacy Agreement */}
                      <motion.div
                        className="mt-6 pt-6 border-t border-[#F0EDE8]"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="agreePrivacy"
                            checked={form.agreePrivacy}
                            onChange={handleChange}
                            className="mt-0.5 w-4 h-4 rounded border-[#E8E5E0] text-[#C4956A] focus:ring-[#C4956A] cursor-pointer accent-[#C4956A]"
                          />
                          <span className="text-xs text-[#8C8780] leading-relaxed">
                            개인정보 수집 및 이용에 동의합니다. 입력하신 정보는 상담 예약 및 안내 목적으로만 사용되며, 상담 완료 후 안전하게 파기됩니다.
                            <a href="/privacy" className="text-[#C4956A] underline ml-1">개인정보처리방침</a>
                          </span>
                        </label>
                      </motion.div>

                      {/* Error Message */}
                      <AnimatePresence>
                        {(submitStatus === "error" || formError) && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                          >
                            {formError || "제출 중 오류가 발생했습니다. 다시 시도해주세요."}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Submit Button */}
                      <motion.div
                        className="mt-8 flex justify-center"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.4 }}
                      >
                        {/* Honeypot - do not remove */}
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form_aux_wrapper" />
                        <motion.button
                          type="submit"
                          disabled={!isStep2Valid || submitStatus === "loading"}
                          whileHover={isStep2Valid && submitStatus !== "loading" ? { scale: 1.04 } : {}}
                          whileTap={isStep2Valid && submitStatus !== "loading" ? { scale: 0.96 } : {}}
                          className="px-10 py-3.5 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {submitStatus === "loading" ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              신청 중...
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-line mr-2"></i>
                              상담 신청하기
                            </>
                          )}
                        </motion.button>
                      </motion.div>
                    </motion.form>
                  )}

                  {/* Step 2: Emergency Form */}
                  {step === 2 && isEmergency && (
                    <motion.form
                      key="step2-emergency"
                      ref={formRef}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35, ease: "easeOut" as const }}
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="p-6 md:p-8"
                    >
                      <motion.button
                        type="button"
                        onClick={() => { setStep(1); setEmergencySlot(null); }}
                        className="flex items-center gap-1.5 text-sm text-[#8C8780] hover:text-[#C4956A] transition-colors mb-6 cursor-pointer"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                      >
                        <i className="ri-arrow-left-line"></i>
                        상담 유형 다시 선택
                      </motion.button>

                      {/* Emergency Header */}
                      <motion.div
                        className="bg-rose-50 rounded-xl p-5 border border-rose-200 mb-6"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center flex-shrink-0">
                            <i className="ri-phone-fill text-white text-lg"></i>
                          </div>
                          <div>
                            <h3 className="text-base font-bold text-[#1C1917] whitespace-nowrap">입찰 전 15분 원포인트 전화상담</h3>
                            <p className="text-xs text-rose-600 font-medium">39,000원 · 카카오페이/계좌이체</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Time Slot Selection */}
                      <motion.div
                        className="mb-6"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.35 }}
                      >
                        <label className="block text-sm font-medium text-[#1C1917] mb-3">
                          가능한 시간을 선택해주세요 <span className="text-red-400">*</span>
                        </label>
                        {emergencySlots.length > 0 ? (
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {emergencySlots.map((slot) => {
                              const slotKey = `${slot.date}|${slot.time}`;
                              const isSelected = emergencySlot === slotKey;
                              return (
                                <motion.button
                                  key={slotKey}
                                  type="button"
                                  onClick={() => setEmergencySlot(slotKey)}
                                  whileHover={{ scale: 1.04 }}
                                  whileTap={{ scale: 0.96 }}
                                  className={`px-4 py-3 rounded-xl border-2 text-center transition-all cursor-pointer ${
                                    isSelected
                                      ? "border-rose-400 bg-rose-50"
                                      : "border-[#E8E5E0] bg-white hover:border-rose-300 hover:bg-rose-50/50"
                                  }`}
                                >
                                  <p className="text-[10px] text-[#8C8780] mb-0.5 whitespace-nowrap">{slot.label}</p>
                                  <p className="text-base font-bold text-[#1C1917] whitespace-nowrap">{slot.time}</p>
                                  <p className="text-[10px] text-[#8C8780] whitespace-nowrap">{slot.dayOfWeek}요일</p>
                                </motion.button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="bg-[#FAFAF8] rounded-xl p-5 border border-[#E8E5E0] text-center">
                            <i className="ri-time-line text-2xl text-[#B8B3AE] mb-2 block"></i>
                            <p className="text-sm text-[#8C8780]">현재 예약 가능한 시간이 없습니다.</p>
                            <p className="text-xs text-[#B8B3AE] mt-1">평일 오전 8:30-9:30, 오후 8:00-10:00에 이용 가능합니다.</p>
                          </div>
                        )}
                      </motion.div>

                      <AnimatePresence>
                        {emergencySlot && emergencySlotData && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.35, ease: "easeOut" as const }}
                            className="overflow-hidden"
                          >
                            <div className="border-t border-[#F0EDE8] pt-5 space-y-4">
                              <p className="text-sm font-medium text-[#1C1917]">
                                {emergencySlotData.date} ({emergencySlotData.dayOfWeek}요일) {emergencySlotData.time} —{" "}
                                <span className="text-[#8C8780]">사전 정보를 입력해주세요</span>
                              </p>

                              <motion.div variants={formFieldItem} initial="hidden" animate="visible">
                                <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                                  이름 <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  name="name"
                                  value={form.name}
                                  onChange={handleChange}
                                  placeholder="이름을 입력해주세요"
                                  className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-rose-400 transition-colors"
                                  required
                                />
                              </motion.div>

                              <motion.div variants={formFieldItem} initial="hidden" animate="visible">
                                <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                                  연락처 <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="tel"
                                  name="phone"
                                  value={form.phone}
                                  onChange={handleChange}
                                  placeholder="010-0000-0000"
                                  className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-rose-400 transition-colors"
                                  required
                                />
                              </motion.div>

                              <motion.div variants={formFieldItem} initial="hidden" animate="visible">
                                <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                                  경매 사건번호 <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  name="caseNumber"
                                  value={form.caseNumber}
                                  onChange={handleChange}
                                  placeholder="예: 2026타경1234"
                                  className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-rose-400 transition-colors"
                                  required
                                />
                              </motion.div>

                              <motion.div variants={formFieldItem} initial="hidden" animate="visible">
                                <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                                  예상 입찰가 <span className="text-red-400">*</span>
                                </label>
                                <input
                                  type="text"
                                  name="bidAmount"
                                  value={form.bidAmount}
                                  onChange={handleChange}
                                  placeholder="예: 2억 5천만원"
                                  className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-rose-400 transition-colors"
                                  required
                                />
                              </motion.div>

                              <motion.div variants={formFieldItem} initial="hidden" animate="visible">
                                <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                                  가장 걱정되는 점 <span className="text-red-400">*</span>
                                </label>
                                <textarea
                                  name="concern"
                                  value={form.concern}
                                  onChange={handleChange}
                                  placeholder="입찰 전 궁금한 점이나 불안한 부분을 간단히 적어주세요"
                                  maxLength={300}
                                  rows={3}
                                  className="w-full px-4 py-3 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-rose-400 transition-colors resize-none"
                                ></textarea>
                                <p className="text-xs text-[#B8B3AE] mt-1">{form.concern.length}/300</p>
                              </motion.div>

                              <motion.div
                                className="bg-[#FAFAF8] rounded-lg p-3 border border-[#E8E5E0]"
                                variants={formFieldItem}
                                initial="hidden"
                                animate="visible"
                              >
                                <p className="text-[11px] text-[#8C8780] leading-relaxed">
                                  입력해 주신 정보는 상담 전 선생님이 미리 검토하는 용도로만 사용되며, 상담 종료 후 안전하게 파기됩니다.
                                </p>
                              </motion.div>

                              {/* Privacy for emergency */}
                              <motion.div
                                className="pt-4 border-t border-[#F0EDE8]"
                                variants={formFieldItem}
                                initial="hidden"
                                animate="visible"
                              >
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    name="agreePrivacy"
                                    checked={form.agreePrivacy}
                                    onChange={handleChange}
                                    className="mt-0.5 w-4 h-4 rounded border-[#E8E5E0] text-rose-500 focus:ring-rose-500 cursor-pointer accent-rose-500"
                                  />
                                  <span className="text-xs text-[#8C8780] leading-relaxed">
                                    개인정보 수집 및 이용에 동의합니다.
                                    <a href="/privacy" className="text-[#C4956A] underline ml-1">개인정보처리방침</a>
                                  </span>
                                </label>
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Error Message */}
                      <AnimatePresence>
                        {(submitStatus === "error" || formError) && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                          >
                            {formError || "제출 중 오류가 발생했습니다. 다시 시도해주세요."}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Submit */}
                      <motion.div
                        className="mt-6 flex justify-center"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                      >
                        {/* Honeypot - do not remove */}
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form_aux_wrapper" />
                        <motion.button
                          type="submit"
                          disabled={!isStep2Valid || submitStatus === "loading"}
                          whileHover={isStep2Valid && submitStatus !== "loading" ? { scale: 1.04 } : {}}
                          whileTap={isStep2Valid && submitStatus !== "loading" ? { scale: 0.96 } : {}}
                          className="w-full max-w-[400px] py-3.5 text-sm font-bold bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {submitStatus === "loading" ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              예약 중...
                            </>
                          ) : (
                            <>
                              <i className="ri-check-double-line mr-2"></i>
                              긴급 상담 예약하기
                            </>
                          )}
                        </motion.button>
                      </motion.div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bottom Info Cards */}
          {submitStatus !== "success" && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10"
              variants={cardStagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
            >
              {[
                {
                  icon: "ri-phone-line",
                  title: "전화 상담",
                  desc: "평일 오전 9시부터 오후 6시까지 전화 상담이 가능합니다. 급한 문의는 전화로 바로 연락주세요.",
                },
                {
                  icon: "ri-kakao-talk-fill",
                  title: "카카오톡 상담",
                  desc: "카카오톡 채널 '강스터디'를 추가하시면 실시간 채팅 상담이 가능합니다. 평일/주말 모두 가능합니다.",
                },
                {
                  icon: "ri-map-pin-line",
                  title: "방문 상담",
                  desc: "사전 예약 후 오프라인 방문 상담도 가능합니다. 강남 사무실에서 편안하게 상담받으세요.",
                },
              ].map((info, idx) => (
                <motion.div
                  key={idx}
                  variants={cardItem}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-xl border border-[#E8E5E0] p-5 transition-colors hover:border-[#C4956A]/30 cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#C4956A]/10 flex items-center justify-center mb-3">
                    <i className={`${info.icon} text-lg text-[#C4956A]`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-[#1C1917] mb-1.5">{info.title}</h4>
                  <p className="text-xs text-[#8C8780]">{info.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}